import React from 'react';
import { Link } from 'react-router-dom';
import {
    Card,
    Table,
    Tabs,
    Input,
    Button,
    Space,
    message,
    Spin,
    Tag,
    Alert,
} from 'antd';
import { ReloadOutlined, StarOutlined, PlusOutlined } from '@ant-design/icons';
import {
    listWatchlist,
    compareFunds,
    addWatchlist,
    removeWatchlist,
} from '../../api/fund';
import { formatReturnRatio, formatNav } from '../../utils/format';
import { FUND_PLACEHOLDER_NAME } from '../../constants/fundConstants';
import './FundBrowser.css';

const watchColumns = (onWatchToggle) => [
    {
        title: '代码',
        dataIndex: 'fundCode',
        width: 100,
        render: (code) => <Link to={`/admin/funds/${code}`}>{code}</Link>,
    },
    {
        title: '名称',
        dataIndex: 'fundName',
        ellipsis: true,
        render: (name) =>
            name === FUND_PLACEHOLDER_NAME ? (
                <Space>
                    <span>{name}</span>
                    <Tag color="orange">待 Agent 同步</Tag>
                </Space>
            ) : (
                name
            ),
    },
    { title: '类型', dataIndex: 'fundType', width: 90 },
    {
        title: '最新净值',
        dataIndex: 'latestNav',
        width: 100,
        render: (v) => (v != null ? formatNav(v) : '—'),
    },
    {
        title: '近1月',
        dataIndex: 'return1m',
        width: 90,
        render: (v) => (v != null ? formatReturnRatio(v) : '—'),
    },
    {
        title: '操作',
        key: 'action',
        width: 100,
        render: (_, row) => (
            <Button
                type="link"
                size="small"
                icon={<StarOutlined />}
                onClick={() => onWatchToggle(row.fundCode)}
            >
                移出关注
            </Button>
        ),
    },
];

class FundBrowser extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'watchlist',
            loading: false,
            addCode: '',
            adding: false,
            watchlist: [],
            compareCodes: '',
            compareResult: null,
            compareLoading: false,
        };
    }

    componentDidMount() {
        this.loadWatchlist();
    }

    loadWatchlist = async () => {
        this.setState({ loading: true });
        try {
            const watchlist = await listWatchlist();
            this.setState({ watchlist });
        } catch (e) {
            message.error(e.message || '加载关注列表失败');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleAddWatch = async () => {
        const code = this.state.addCode.trim();
        if (!code) {
            message.warning('请输入基金代码');
            return;
        }
        this.setState({ adding: true });
        try {
            await addWatchlist(code);
            message.success('已加入关注，Agent 将同步数据');
            this.setState({ addCode: '' });
            await this.loadWatchlist();
        } catch (e) {
            message.error(e.message || '加入关注失败');
        } finally {
            this.setState({ adding: false });
        }
    };

    handleRemoveWatch = async (code) => {
        try {
            await removeWatchlist(code);
            message.success('已移出关注');
            await this.loadWatchlist();
        } catch (e) {
            message.error(e.message || '操作失败');
        }
    };

    handleCompare = async () => {
        const codes = this.state.compareCodes
            .split(/[,，\s]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        if (codes.length < 2) {
            message.warning('请至少输入两个已关注的基金代码，逗号分隔');
            return;
        }
        this.setState({ compareLoading: true });
        try {
            const compareResult = await compareFunds(codes);
            this.setState({ compareResult });
        } catch (e) {
            message.error(e.message || '对比失败，请确认代码均在关注列表中');
        } finally {
            this.setState({ compareLoading: false });
        }
    };

    fillCompareFromWatchlist = () => {
        const codes = this.state.watchlist.map((f) => f.fundCode).join(',');
        this.setState({ compareCodes: codes });
    };

    renderCompare() {
        const { compareResult, compareLoading, compareCodes, watchlist } = this.state;
        const metrics = compareResult?.metrics || {};
        const codes = compareResult?.fundCodes || Object.keys(metrics);

        return (
            <div>
                <Space wrap className="fund-toolbar">
                    <Input
                        className="fund-compare-codes"
                        placeholder="输入已关注基金代码，逗号分隔"
                        value={compareCodes}
                        onChange={(e) => this.setState({ compareCodes: e.target.value })}
                    />
                    <Button
                        disabled={watchlist.length < 2}
                        onClick={this.fillCompareFromWatchlist}
                    >
                        填入全部关注
                    </Button>
                    <Button type="primary" loading={compareLoading} onClick={this.handleCompare}>
                        对比
                    </Button>
                </Space>
                {codes.length > 0 && (
                    <Table
                        size="small"
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        rowKey="code"
                        dataSource={codes.map((code) => ({
                            code,
                            ...(metrics[code] || {}),
                        }))}
                        columns={[
                            {
                                title: '代码',
                                dataIndex: 'code',
                                render: (c) => <Link to={`/admin/funds/${c}`}>{c}</Link>,
                            },
                            {
                                title: '近1月',
                                dataIndex: 'return1m',
                                render: formatReturnRatio,
                            },
                            {
                                title: '近3月',
                                dataIndex: 'return3m',
                                render: formatReturnRatio,
                            },
                            {
                                title: '近1年',
                                dataIndex: 'return1y',
                                render: formatReturnRatio,
                            },
                            {
                                title: '最大回撤',
                                dataIndex: 'maxDrawdown',
                                render: formatReturnRatio,
                            },
                        ]}
                    />
                )}
                {compareResult?.disclaimer && (
                    <p className="fund-disclaimer">{compareResult.disclaimer}</p>
                )}
            </div>
        );
    }

    render() {
        const { activeTab, loading, addCode, adding, watchlist } = this.state;
        const cols = watchColumns(this.handleRemoveWatch);

        const tabItems = [
            {
                key: 'watchlist',
                label: '我的关注',
                children: (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            message="数据由 AI Agent 推送至系统。加入关注后请稍候刷新；名称为「待同步」表示 Agent 尚未写入完整档案。"
                            style={{ marginBottom: 12 }}
                        />
                        <Space wrap className="fund-toolbar">
                            <Input
                                placeholder="输入基金代码，如 110011"
                                value={addCode}
                                onChange={(e) => this.setState({ addCode: e.target.value })}
                                onPressEnter={this.handleAddWatch}
                                style={{ width: 200 }}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                loading={adding}
                                onClick={this.handleAddWatch}
                            >
                                加入关注
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={this.loadWatchlist}>
                                刷新
                            </Button>
                        </Space>
                        <Spin spinning={loading}>
                            <Table
                                rowKey="fundCode"
                                size="small"
                                scroll={{ x: 'max-content' }}
                                columns={cols}
                                dataSource={watchlist}
                                pagination={false}
                                locale={{ emptyText: '暂无关注基金，请在上方输入代码加入' }}
                            />
                        </Spin>
                    </>
                ),
            },
            {
                key: 'compare',
                label: '对比',
                children: this.renderCompare(),
            },
        ];

        return (
            <div className="fund-browser">
                <Card title="我的基金" bordered={false}>
                    <Tabs
                        activeKey={activeTab}
                        items={tabItems}
                        onChange={(activeTab) => this.setState({ activeTab })}
                    />
                    <p className="fund-disclaimer">数据仅供参考，不构成投资建议。</p>
                </Card>
            </div>
        );
    }
}

export default FundBrowser;
